import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Input, Label, Badge, Spinner, Table, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup } from 'reactstrap';
import { Breadcrumbs } from '../../../AbstractElements';
import { toast } from 'react-toastify';
import axios from 'axios';
import API_BASE_URL from '../../../Config/api';
import authHeader from '../../Customer/authHeader';
import CustomRangeReport from './CustomRangeReport';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TIMEZONES = ['Europe/London', 'Europe/Dublin', 'Europe/Paris', 'Europe/Berlin', 'Europe/Athens', 'Asia/Kolkata', 'America/New_York', 'America/Los_Angeles', 'UTC'];
const MONTH_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);
const EMAIL_RE = /^[^\s@<>"',;]+@[^\s@<>"',;]+\.[^\s@<>"',;]+$/;

const api = (path) => `${API_BASE_URL}/api${path}`;
const errMsg = (e, fallback) => (e.response && e.response.data && e.response.data.error) || fallback;

const ReportEmails = () => {
  const [recipients, setRecipients] = useState([]);
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [savingSettings, setSavingSettings] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingType, setSendingType] = useState(null);

  // modal: null = closed, {} = add, {id,...} = edit
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '' });
  const [savingRecipient, setSavingRecipient] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, s, l] = await Promise.all([
        axios.get(api('/report-recipients'), authHeader()),
        axios.get(api('/report-email/settings'), authHeader()),
        axios.get(api('/report-email/logs'), authHeader()),
      ]);
      setRecipients(r.data.recipients || []);
      setSettings(s.data.settings);
      setLogs(l.data.logs || []);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load report email settings (admin access required)'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allSelected = recipients.length > 0 && selected.size === recipients.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(recipients.map((r) => r.id)));
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const activeCount = useMemo(() => recipients.filter((r) => r.is_active).length, [recipients]);

  const openAdd = () => { setForm({ name: '', email: '' }); setEditing({}); };
  const openEdit = (r) => { setForm({ name: r.name || '', email: r.email }); setEditing(r); };

  const saveRecipient = async () => {
    const email = form.email.trim();
    if (!EMAIL_RE.test(email)) return toast.error('Enter a valid email address');
    setSavingRecipient(true);
    try {
      if (editing.id) {
        await axios.put(api(`/report-recipients/${editing.id}`), { name: form.name, email }, authHeader());
        toast.success('Recipient updated');
      } else {
        await axios.post(api('/report-recipients'), { name: form.name, email }, authHeader());
        toast.success('Recipient added');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to save recipient'));
    } finally {
      setSavingRecipient(false);
    }
  };

  const toggleActive = async (r) => {
    try {
      await axios.put(api(`/report-recipients/${r.id}`), { is_active: !r.is_active }, authHeader());
      setRecipients((prev) => prev.map((x) => (x.id === r.id ? { ...x, is_active: r.is_active ? 0 : 1 } : x)));
    } catch (e) {
      toast.error(errMsg(e, 'Failed to update recipient'));
    }
  };

  const deleteIds = async (ids) => {
    if (ids.length === 0) return;
    const what = ids.length === 1 ? 'this recipient' : `${ids.length} recipients`;
    if (!window.confirm(`Delete ${what}? They will no longer receive the weekly or monthly report.`)) return;
    try {
      await axios.post(api('/report-recipients/bulk-delete'), { ids }, authHeader());
      toast.success('Deleted');
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to delete'));
    }
  };

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    setSavingSettings(true);
    try {
      const res = await axios.put(api('/report-email/settings'), {
        enabled: !!next.enabled, send_day: next.send_day, send_time: next.send_time, timezone: next.timezone,
        monthly_enabled: !!next.monthly_enabled, monthly_send_day: next.monthly_send_day, monthly_send_time: next.monthly_send_time,
      }, authHeader());
      setSettings(res.data.settings);
      toast.success('Schedule saved');
    } catch (e) {
      toast.error(errMsg(e, 'Failed to save schedule'));
      load();
    } finally {
      setSavingSettings(false);
    }
  };

  const sendNow = async (type = 'weekly') => {
    const ids = [...selected];
    if (ids.length === 0) return toast.error('Select at least one recipient');
    if (!window.confirm(`Send the latest ${type} report now to ${ids.length} selected recipient(s)?`)) return;
    setSending(true);
    setSendingType(type);
    try {
      const res = await axios.post(api('/report-email/send-now'), { ids, type }, authHeader());
      const { sent, failed } = res.data;
      if (failed) toast.warn(`Sent ${sent}, failed ${failed}. See the log below.`);
      else toast.success(`Sent to ${sent} recipient(s)`);
      load();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to send'));
    } finally {
      setSending(false);
      setSendingType(null);
    }
  };

  const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-GB') : '-');

  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs parent="Settings" title="Report Emails" />
        <Container fluid><div className="text-center py-5"><Spinner /></div></Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumbs parent="Settings" title="Report Emails" />
      <Container fluid>
        {settings && (
          <Row>
            <Col sm="12">
              <Card>
                <CardHeader className="pb-0">
                  <h5>Automatic Monday Report</h5>
                  <p className="text-muted mb-0">
                    Each active recipient gets the previous week's report (Monday to Sunday) as a separate email, with an Excel attachment.
                  </p>
                </CardHeader>
                <CardBody>
                  <Row className="align-items-end g-3">
                    <Col md="3">
                      <div className="form-check form-switch">
                        <Input
                          type="switch"
                          id="report-enabled"
                          checked={!!settings.enabled}
                          disabled={savingSettings}
                          onChange={(e) => saveSettings({ enabled: e.target.checked ? 1 : 0 })}
                        />
                        <Label for="report-enabled" className="form-check-label">
                          {settings.enabled ? 'Enabled' : 'Disabled'}
                        </Label>
                      </div>
                      {settings.enabled && activeCount === 0 && (
                        <small className="text-danger">No active recipients: nothing will be sent.</small>
                      )}
                    </Col>
                    <Col md="2">
                      <Label>Day</Label>
                      <Input type="select" value={settings.send_day} disabled={savingSettings}
                        onChange={(e) => saveSettings({ send_day: Number(e.target.value) })}>
                        {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                      </Input>
                    </Col>
                    <Col md="2">
                      <Label>Time</Label>
                      <Input type="time" value={settings.send_time} disabled={savingSettings}
                        onChange={(e) => e.target.value && saveSettings({ send_time: e.target.value })} />
                    </Col>
                    <Col md="3">
                      <Label>Timezone</Label>
                      <Input type="select" value={settings.timezone} disabled={savingSettings}
                        onChange={(e) => saveSettings({ timezone: e.target.value })}>
                        {(TIMEZONES.includes(settings.timezone) ? TIMEZONES : [settings.timezone, ...TIMEZONES]).map((tz) => (
                          <option key={tz} value={tz}>{tz}</option>
                        ))}
                      </Input>
                    </Col>
                    <Col md="2">
                      <small className="text-muted">
                        Last sent: {settings.last_sent_date ? new Date(settings.last_sent_date).toLocaleDateString('en-GB') : 'never'}
                      </small>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {settings && (
          <Row>
            <Col sm="12">
              <Card>
                <CardHeader className="pb-0">
                  <h5>Automatic Monthly Report</h5>
                  <p className="text-muted mb-0">
                    Each active recipient gets the previous calendar month's report as a separate email, with an Excel attachment.
                    The same recipients and timezone as the weekly report are used.
                  </p>
                </CardHeader>
                <CardBody>
                  <Row className="align-items-end g-3">
                    <Col md="3">
                      <div className="form-check form-switch">
                        <Input
                          type="switch"
                          id="monthly-report-enabled"
                          checked={!!settings.monthly_enabled}
                          disabled={savingSettings}
                          onChange={(e) => saveSettings({ monthly_enabled: e.target.checked ? 1 : 0 })}
                        />
                        <Label for="monthly-report-enabled" className="form-check-label">
                          {settings.monthly_enabled ? 'Enabled' : 'Disabled'}
                        </Label>
                      </div>
                      {settings.monthly_enabled && activeCount === 0 && (
                        <small className="text-danger">No active recipients: nothing will be sent.</small>
                      )}
                    </Col>
                    <Col md="2">
                      <Label>Day of month</Label>
                      <Input type="select" value={settings.monthly_send_day} disabled={savingSettings}
                        onChange={(e) => saveSettings({ monthly_send_day: Number(e.target.value) })}>
                        {MONTH_DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                      </Input>
                    </Col>
                    <Col md="2">
                      <Label>Time</Label>
                      <Input type="time" value={settings.monthly_send_time} disabled={savingSettings}
                        onChange={(e) => e.target.value && saveSettings({ monthly_send_time: e.target.value })} />
                    </Col>
                    <Col md="3">
                      <Label>Timezone</Label>
                      <Input type="text" value={settings.timezone} disabled readOnly />
                    </Col>
                    <Col md="2">
                      <small className="text-muted">
                        Last sent: {settings.monthly_last_sent_date ? new Date(settings.monthly_last_sent_date).toLocaleDateString('en-GB') : 'never'}
                      </small>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {settings && (
          <CustomRangeReport
            timezone={settings.timezone}
            recipients={recipients}
            selectedIds={selected}
            onSent={load}
          />
        )}

        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h5 className="mb-0">Recipients <Badge color="primary">{recipients.length}</Badge></h5>
                <div className="d-flex flex-wrap gap-2">
                  <Button color="primary" onClick={openAdd}>Add recipient</Button>
                  <Button color="success" outline disabled={selected.size === 0 || sending} onClick={() => sendNow('weekly')}>
                    {sendingType === 'weekly' ? 'Sending...' : `Send weekly now to selected (${selected.size})`}
                  </Button>
                  <Button color="success" outline disabled={selected.size === 0 || sending} onClick={() => sendNow('monthly')}>
                    {sendingType === 'monthly' ? 'Sending...' : `Send monthly now to selected (${selected.size})`}
                  </Button>
                  <Button color="danger" outline disabled={selected.size === 0} onClick={() => deleteIds([...selected])}>
                    Delete selected
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {recipients.length === 0 ? (
                  <p className="text-muted text-center py-3">No recipients yet. Add the people who should receive the weekly and monthly reports.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-border-horizontal">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}><Input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Active</th>
                          <th style={{ width: 160 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipients.map((r) => (
                          <tr key={r.id}>
                            <td><Input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} /></td>
                            <td>{r.name || '-'}</td>
                            <td>{r.email}</td>
                            <td>
                              <div className="form-check form-switch">
                                <Input type="switch" checked={!!r.is_active} onChange={() => toggleActive(r)} />
                              </div>
                            </td>
                            <td>
                              <Button size="sm" color="primary" outline className="me-2" onClick={() => openEdit(r)}>Edit</Button>
                              <Button size="sm" color="danger" outline onClick={() => deleteIds([r.id])}>Delete</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className="pb-0"><h5>Recent sends</h5></CardHeader>
              <CardBody>
                {logs.length === 0 ? (
                  <p className="text-muted text-center py-3">Nothing sent yet.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-border-horizontal">
                      <thead>
                        <tr><th>Sent</th><th>Recipient</th><th>Report</th><th>Period</th><th>Trigger</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {logs.map((l) => (
                          <tr key={l.id}>
                            <td>{fmtDateTime(l.sent_at)}</td>
                            <td>{l.recipient_email}</td>
                            <td>{l.report_type === 'monthly' ? 'Monthly' : l.report_type === 'custom' ? 'Custom range' : 'Weekly'}</td>
                            <td>{l.period_start ? `${new Date(l.period_start).toLocaleDateString('en-GB')} - ${new Date(l.period_end).toLocaleDateString('en-GB')}` : '-'}</td>
                            <td>{l.trigger_type}</td>
                            <td>
                              <Badge color={l.status === 'sent' ? 'success' : 'danger'} title={l.error_message || ''}>{l.status}</Badge>
                              {l.error_message && <small className="text-danger ms-2">{l.error_message}</small>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={editing !== null} toggle={() => setEditing(null)} centered>
        <ModalHeader toggle={() => setEditing(null)}>{editing && editing.id ? 'Edit recipient' : 'Add recipient'}</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Name (optional)</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label>Email</Label>
            <Input type="email" value={form.email} autoFocus
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && saveRecipient()} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setEditing(null)}>Cancel</Button>
          <Button color="primary" disabled={savingRecipient} onClick={saveRecipient}>
            {savingRecipient ? 'Saving...' : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default ReportEmails;
